import { useState, useEffect, useCallback } from "react";
import { X, Share2, Plus, Sparkles } from "lucide-react";

const DISMISS_KEY = "myasher-pwa-prompt-dismissed";
const DISMISS_DAYS = 30;

function isIOS() {
  if (typeof window === "undefined") return false;
  return /iP(hone|ad|od)/.test(window.navigator.userAgent) && !window.MSStream;
}

function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState(null); // 'native' | 'ios'

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private mode noop */
    }
  }, []);

  useEffect(() => {
    if (isRunningStandalone() || wasRecentlyDismissed()) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode("native");
      // Delay slightly so user has time to read the page first
      setTimeout(() => setVisible(true), 12000);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // iOS Safari doesn't fire beforeinstallprompt — show manual hint after a delay
    if (isIOS()) {
      setMode("ios");
      const t = setTimeout(() => setVisible(true), 18000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      dismiss();
    } else {
      // User declined — treat as dismissed so we don't nag
      dismiss();
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Install Tyvena"
      className="fixed bottom-4 left-4 right-4 md:right-auto md:left-6 md:bottom-6 md:max-w-sm z-50 animate-fade-in"
    >
      <div className="relative bg-white border border-[#E2E8F0] rounded-2xl shadow-2xl overflow-hidden">
        {/* Accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-primary via-secondary to-primary" aria-hidden="true" />

        <div className="p-4 md:p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl overflow-hidden shadow-sm ring-2 ring-primary/20">
              <img src="/pwa-icon-192.png" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-accent text-base leading-tight">
                Install Tyvena
              </p>
              <p className="text-xs text-text-secondary font-body mt-0.5 leading-snug">
                {mode === "ios"
                  ? "Add Tyvena to your home screen for quick, app-like access."
                  : "Add Tyvena to your home screen. Opens instantly, no browser bar."}
              </p>
            </div>
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              aria-label="Dismiss install prompt"
              className="flex-shrink-0 w-8 h-8 rounded-full text-text-secondary hover:bg-mist hover:text-text-primary transition-colors flex items-center justify-center -mt-1 -mr-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {mode === "native" && (
            <div className="mt-3 flex gap-2">
              <button
                data-testid="pwa-install-btn"
                onClick={handleInstall}
                className="flex-1 bg-primary text-white hover:bg-primary-dark rounded-full font-body font-bold text-sm py-2.5 flex items-center justify-center gap-2 shadow-md transition-all active:scale-95"
              >
                <Sparkles className="w-4 h-4" />
                Install
              </button>
              <button
                onClick={dismiss}
                className="px-4 rounded-full text-text-secondary hover:bg-mist font-body font-semibold text-sm transition-colors"
              >
                Not now
              </button>
            </div>
          )}

          {mode === "ios" && (
            <div className="mt-3 space-y-2 text-xs font-body text-text-primary bg-mist rounded-xl p-3">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center font-bold text-[10px]">1</span>
                <span>Tap the <Share2 className="inline w-3.5 h-3.5 text-primary" aria-label="share icon" /> Share icon at the bottom of Safari</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center font-bold text-[10px]">2</span>
                <span>Scroll down and tap <Plus className="inline w-3.5 h-3.5 text-primary" /> <strong>Add to Home Screen</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
